/* eslint-disable react-refresh/only-export-components -- re-exports
   selectInlineFields and isInlineFieldEligible from cardConfig */
/**
 * Generic pipeline card content for non-deal entity types.
 *
 * Mirrors the visual layout of DealCardContent but selects the data to
 * render dynamically from the entity's property schema. Reuses the
 * shared title row, association line, and footer from PipelineCardParts;
 * the only generic-specific bit is the value row of inline property
 * fields chosen by `selectInlineFields`.
 *
 * Layout matches the deal card:
 * ┌─────────────────────────────────┐
 * │ Entity Name                     │
 * │ 🏢 Primary association          │  ← company > contact > skip
 * │                                 │
 * │ {field 1}    📅 {field 2}      │  ← up to MAX_INLINE_FIELDS rows
 * │ ─────────────────────────────── │
 * │ 👤 Owner          🕐 Updated   │
 * └─────────────────────────────────┘
 */

import { memo, useMemo } from 'react';

import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { selectInlineFields } from '../utils/cardConfig';
import { CardFooter, CardTitleRow, getEntityName } from './PipelineCardParts';

export { isInlineFieldEligible, selectInlineFields } from '../utils/cardConfig';

// ---------------------------------------------------------------------------
// InlineField — one value entry in the value row.
// Renders nothing when the value is unset so the row collapses gracefully.
// ---------------------------------------------------------------------------

const InlineField = memo(function InlineField({
  object,
  schema,
}: {
  object: CrmObject;
  schema: PropertyTypeSchema;
}) {
  const value = getPropertyValue<unknown>(
    object.properties?.[schema.property_type_name]
  );
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return (
    <Tooltip title={schema.label || schema.property_type_name}>
      <div className='text-sm text-[var(--color-text-secondary)] min-w-0 max-w-full truncate'>
        <PropertyValueLabel
          value={value}
          propertyType={schema}
          entityProperties={object.properties}
          orgId={object.org_id}
          fontSize='sm'
        />
      </div>
    </Tooltip>
  );
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface GenericCardContentProps {
  /** The entity object */
  object: CrmObject;
  /** Property schemas for this entity type. Used to choose & render fields. */
  propertySchemas: Record<string, PropertyTypeSchema>;
  /** Associated company (first company association), if any */
  company?: CrmObject;
  /** Associated contact (first contact association), used when no company */
  contact?: CrmObject;
  /** Associated owner (user), if any */
  owner?: CrmObject;
}

export const GenericCardContent = memo(function GenericCardContent({
  object,
  propertySchemas,
  company,
  contact,
  owner,
}: GenericCardContentProps) {
  const name = getEntityName(object, 'Untitled');

  const inlineFields = useMemo(
    () => selectInlineFields(propertySchemas),
    [propertySchemas]
  );

  const updatedAt = useMemo(() => {
    const raw = getPropertyValue<unknown>(object.properties?.ahoy_updated_at);
    return raw !== null && raw !== undefined ? String(raw) : null;
  }, [object.properties?.ahoy_updated_at]);

  return (
    <div>
      <CardTitleRow title={name} company={company} contact={contact} />

      {/* ── Value row: up to MAX_INLINE_FIELDS scalar fields. Wraps to a
            second line when needed so cards don't overflow horizontally. */}
      {inlineFields.length > 0 && (
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 px-3 mt-3 min-w-0'>
          {inlineFields.map(schema => (
            <InlineField
              key={schema.property_type_name}
              object={object}
              schema={schema}
            />
          ))}
        </div>
      )}

      <CardFooter owner={owner} updatedAt={updatedAt} />
    </div>
  );
});
